import React, { useMemo, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { IUser } from '@whitelabel/helpers/types';
import { ISidebarNavItem, IPDSPolicy } from '../../../helpers/types';
import { contentBlocksToNavItems } from '../../../helpers/utils';
import PDSInsuredCard from '../PDSInsuredCard';
import SidebarNav from '../../SidebarNav';
import ResponsiveBenefits from '../../ResponsiveBenefits';
import commonMessages from '../../ResponsiveBenefits/messages';
import { getHeaderMessageKey } from '../../ResponsiveBenefits/helper';
import { StyledCardBlock, StyledContentBlock, StyledHeadingBlock } from './styledPDSPolicyContent';
import messages from './messages';

interface IPDSPolicyContentProps {
  currency: string;
  policy: IPDSPolicy;
  activePolicy: string;
  user?: IUser;
  partnerTransactionId?: string;
  partnerId: string;
}

const PDSPolicyContent = ({
  currency,
  policy: {
    policyType,
    policyVersion,
    insured,
    benefits,
    body,
    attributesArray,
    showBenefits,
    benefitsLabelConfig,
    underwriter: { name: policyUnderwriterName },
  },
  activePolicy,
  user,
  partnerTransactionId,
  partnerId,
}: IPDSPolicyContentProps): JSX.Element => {
  const intl = useIntl();
  const location = useLocation();
  const sidebarItems = useMemo(
    () => contentBlocksToNavItems(body, policyVersion),
    [body, policyVersion],
  ) as ISidebarNavItem[];
  const [activeSidebarItem, setActiveSidebarItem] = useState(sidebarItems[0].key);

  useEffect(() => {
    setActiveSidebarItem(sidebarItems[0].key);
  }, [activePolicy, setActiveSidebarItem, sidebarItems]);

  const blockTypes: Record<string, any> = {
    card: { component: StyledCardBlock, structBlock: false },
    heading: {
      component: StyledHeadingBlock,
      structBlock: true,
      props: { namePrefix: policyVersion, shouldParse: true },
    },
  };

  const benefitsBlockLabelsAndConfig = benefitsLabelConfig ?? {
    benefitsDescriptionLabel: intl.formatMessage(commonMessages.benefitHeader),
    benefitsLimitLabel: intl.formatMessage(commonMessages.limitHeader),
    benefitsExcessLabel: intl.formatMessage(getHeaderMessageKey(partnerId, policyUnderwriterName)),
  };

  const benefitsBlock = {
    component: ResponsiveBenefits,
    structBlock: false,
    props: { benefits, currency, benefitsBlockLabelsAndConfig },
  };

  if (showBenefits) blockTypes.benefits_table = benefitsBlock;

  return (
    <Row>
      <Col tag="aside" id={`Sidebar--${policyVersion}`} xs={12} lg={3}>
        {user && !location.pathname.includes('/pds/parcel') && (
          <PDSInsuredCard
            policyType={policyType}
            insured={insured}
            attributesArray={attributesArray}
            partnerTransactionId={partnerTransactionId}
            partnerId={partnerId}
          />
        )}

        <SidebarNav
          title={intl.formatMessage(messages.sidebarNavTitle)}
          items={sidebarItems}
          activeItem={activeSidebarItem}
          onSetActive={setActiveSidebarItem}
          sticky="lg"
          bottomBoundary={`#Sidebar--${policyVersion}`}
        />
      </Col>

      <Col xs={12} lg={9}>
        <StyledContentBlock blocks={body} blockTypes={blockTypes} />
      </Col>
    </Row>
  );
};

export default PDSPolicyContent;
